import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ChevronRight,
  CircleCheck,
  Copy,
  EllipsisVertical,
  MessageSquare,
  MessagesSquare,
  Play,
  Tag,
} from 'lucide-react';
import {
  Checkbox,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui';
import { Kbd } from '@/components/keyboard/Kbd';
import { useToast } from '@/components/ui/Toast';
import { LabelChips } from './LabelChips';
import { routes } from '@/lib/routes';
import { useLabels } from '@/hooks/useLabels';
import { useRelabelInboxItem } from '@/hooks/useInbox';
import type { InboxItem } from '@/types/inbox';
import { formatRelativeTime } from '@/pages/activity/utils';
import { useInboxItem } from '@/hooks/useInbox';
import { AskUserAnswerCard } from './AskUserAnswerCard';
import { ReviewCard } from './ReviewCard';
import { truncate } from './preview-utils';
import { cleanDisplayText } from '@/utils/display-text';

const COMPACT_PREVIEW_MAX = 180;

interface InboxRowProps {
  item: InboxItem;
  isSelected: boolean;
  isExpanded: boolean;
  isChecked: boolean;
  onSelect: () => void;
  onToggleExpand: () => void;
  onToggleChecked: () => void;
}

export function InboxRow({
  item,
  isSelected,
  isExpanded,
  isChecked,
  onSelect,
  onToggleExpand,
  onToggleChecked,
}: InboxRowProps) {
  const rowRef = useRef<HTMLLIElement>(null);

  // Scroll the selected row into view when selection changes via keyboard.
  useEffect(() => {
    if (isSelected && rowRef.current) {
      rowRef.current.scrollIntoView({ block: 'nearest' });
    }
  }, [isSelected]);

  const { data, isLoading } = useInboxItem(item.inboxItemId, {
    expand: 'source',
    enabled: isExpanded,
  });

  const isReview = item.actionLabel === 'Review';
  const Icon = isReview ? CircleCheck : MessageSquare;

  return (
    <li
      ref={rowRef}
      className={
        'group border-b border-l-2 border-border last:border-b-0 ' +
        // Left border marks the keyboard-focused row independently of any
        // multi-select tint, so both signals can coexist on the same row.
        (isSelected ? 'border-l-primary ' : 'border-l-transparent ') +
        (isChecked
          ? 'bg-primary-50/50 dark:bg-primary-900/10'
          : isSelected
          ? 'bg-accent/30'
          : 'hover:bg-accent/20')
      }
    >
      <div className="flex w-full items-start">
        <div
          className="flex shrink-0 items-start pl-3 pr-1 pt-3.5"
          onClick={(e) => e.stopPropagation()}
        >
          <Checkbox
            checked={isChecked}
            onCheckedChange={() => {
              onSelect();
              onToggleChecked();
            }}
            aria-label={isChecked ? 'Unselect item' : 'Select item'}
            className={
              'transition-opacity ' +
              (isChecked ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100')
            }
          />
        </div>
        <button
          type="button"
          onClick={() => {
            onSelect();
            onToggleExpand();
          }}
          className="focus-ring flex min-w-0 flex-1 items-start gap-3 py-3 pl-2 pr-2 text-left"
          aria-expanded={isExpanded}
        >
          <ChevronRight
            className={
              'mt-1 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform ' +
              (isExpanded ? 'rotate-90' : '')
            }
          />
          <Icon
            className={
              'mt-0.5 h-4 w-4 shrink-0 ' +
              (isReview ? 'text-emerald-500' : 'text-primary-500')
            }
          />
          <div className="min-w-0 flex-1">
            <span className="block truncate text-sm font-medium text-foreground">
              {cleanDisplayText(item.title) || item.agentName || 'Untitled'}
            </span>
            <p className="mt-0.5 truncate text-sm text-muted-foreground">
              {item.agentName ? `${item.agentName} · ` : ''}
              {truncate(cleanDisplayText(item.preview), COMPACT_PREVIEW_MAX)}
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <span
              // pr-[7px] matches a label chip's text inset (px-1.5 + 1px
              // border) so the timestamp's right edge lines up with the chip
              // text below it, not the chip's outer pill edge.
              className="whitespace-nowrap pr-[7px] text-xs text-muted-foreground"
              title={new Date(item.createdAt).toLocaleString(undefined, {
                weekday: 'short',
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: '2-digit',
              })}
            >
              {formatRelativeTime(item.createdAt)}
            </span>
            {item.labelIds && item.labelIds.length > 0 && (
              <LabelChips labelIds={item.labelIds} />
            )}
          </div>
        </button>
        <div
          className="flex shrink-0 items-start pl-1 pr-2 pt-3"
          onClick={(e) => e.stopPropagation()}
        >
          <InboxRowMenu item={item} />
        </div>
      </div>

      {isExpanded && (
        // Left padding mirrors the row title's start: checkbox slot (32px)
        // + button pl-2 + chevron + gap + icon + gap ≈ 95px.
        <div className="px-4 pb-4 pl-[95px]">
          {isReview ? (
            <ReviewCard
              item={item}
              request={data?.source}
              isLoadingRequest={isLoading}
            />
          ) : (
            <AskUserAnswerCard
              item={item}
              request={data?.source}
              isLoadingRequest={isLoading}
            />
          )}
        </div>
      )}
    </li>
  );
}

function InboxRowMenu({ item }: { item: InboxItem }) {
  const toast = useToast();
  const conversationHref = item.surfaceId
    ? `${routes.operator.chat(item.conversationId)}?focus=${item.surfaceId}`
    : routes.operator.chat(item.conversationId);
  const runHref = routes.console.run(item.runId);

  const copyRunId = async () => {
    try {
      await navigator.clipboard.writeText(item.runId);
      toast.success('Run ID copied');
    } catch {
      toast.error('Copy failed');
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          aria-label="More actions"
          className="focus-ring rounded-md p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-muted hover:text-foreground focus:opacity-100 group-hover:opacity-100 data-[state=open]:opacity-100"
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <EllipsisVertical className="h-3.5 w-3.5" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        sideOffset={4}
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <DropdownMenuItem asChild>
          <Link to={conversationHref} onClick={(e) => e.stopPropagation()}>
            <MessagesSquare className="mr-2 h-3.5 w-3.5" />
            Open conversation
            <span className="ml-auto pl-6">
              <Kbd>O</Kbd>
            </span>
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link to={runHref} onClick={(e) => e.stopPropagation()}>
            <Play className="mr-2 h-3.5 w-3.5" />
            Open run detail
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={copyRunId}>
          <Copy className="mr-2 h-3.5 w-3.5" />
          Copy run ID
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <LabelsSubMenu
          inboxItemId={item.inboxItemId}
          spaceId={item.spaceId}
          currentLabelIds={item.labelIds ?? []}
        />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function LabelsSubMenu({
  inboxItemId,
  spaceId,
  currentLabelIds,
}: {
  inboxItemId: string;
  spaceId: string | null | undefined;
  currentLabelIds: string[];
}) {
  const { data: labels } = useLabels(spaceId ?? undefined);
  const relabel = useRelabelInboxItem();
  const disabled = !spaceId;
  const applied = new Set(currentLabelIds);

  return (
    <DropdownMenuSub>
      <DropdownMenuSubTrigger disabled={disabled}>
        <Tag className="mr-2 h-3.5 w-3.5" />
        Labels
      </DropdownMenuSubTrigger>
      <DropdownMenuPortal>
        <DropdownMenuSubContent className="min-w-[12rem]">
          {disabled ? (
            <DropdownMenuItem disabled className="text-xs text-muted-foreground">
              No space — labels unavailable
            </DropdownMenuItem>
          ) : !labels ? (
            <DropdownMenuItem disabled className="text-xs text-muted-foreground">
              Loading…
            </DropdownMenuItem>
          ) : labels.length === 0 ? (
            <DropdownMenuItem disabled className="text-xs text-muted-foreground">
              No labels yet in this space
            </DropdownMenuItem>
          ) : (
            labels.map((label) => {
              const checked = applied.has(label.labelId);
              return (
                <DropdownMenuCheckboxItem
                  key={label.labelId}
                  checked={checked}
                  // Radix would close the menu on select; prevent that so
                  // users can toggle multiple labels in one open.
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(next) => {
                    if (!spaceId) return;
                    relabel.mutate({
                      spaceId,
                      inboxItemId,
                      add: next ? [label.labelId] : undefined,
                      remove: !next ? [label.labelId] : undefined,
                    });
                  }}
                  className="gap-2"
                >
                  <span
                    className="h-2 w-2 shrink-0 rounded-full"
                    style={{ backgroundColor: label.color || 'currentColor' }}
                    aria-hidden
                  />
                  <span className="flex-1 truncate">{label.name}</span>
                </DropdownMenuCheckboxItem>
              );
            })
          )}
        </DropdownMenuSubContent>
      </DropdownMenuPortal>
    </DropdownMenuSub>
  );
}
